import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { usePaystackPayment } from 'react-paystack';
import { Order } from '../types';
import { createOrder } from '.';

const Checkout = () => {
  const { cart, cartTotal, clearCart, updateQuantity, removeFromCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [shippingState, setShippingState] = useState('');
  const [shippingCost, setShippingCost] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('pod');
  
  // Form State for Paystack Integration
  const [details, setDetails] = useState({
    name: user?.name || '',
    email: user?.email || '',
    address: user?.address || '',
    phone: user?.phone || ''
  });

  const receiptRef = useRef<HTMLInputElement>(null);

  // Sync state with user context
  useEffect(() => {
    if (user) {
      setDetails(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        address: user.address || prev.address,
        phone: user.phone || prev.phone
      }));
    }
  }, [user]);

  // Calculate shipping based on state
  useEffect(() => {
    const state = shippingState.toLowerCase().trim();
    if (state === 'kano') setShippingCost(1500);
    else if (state === 'abuja') setShippingCost(3500);
    else if (state === '') setShippingCost(0);
    else setShippingCost(7000);
  }, [shippingState]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDetails({ ...details, [e.target.name]: e.target.value });
  };

  // Finalize Order Logic (Called after payment or for POD)
  const finalizeOrder = async (paymentRef?: string, receiptFile?: File) => {
    const generatedId = `MFP-${Math.floor(100000 + Math.random() * 900000)}`;
    let paymentMsg = "";

    // Upload Receipt if exists
    let receiptURL = "";
    if (receiptFile) {
      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "dkkm3uigr";
      const uploadPreset = process.env.REACT_APP_CLOUDINARY_PRESET || "kano_fresh_preset";
      const formData = new FormData();
      formData.append("file", receiptFile);
      formData.append("upload_preset", uploadPreset);

      try {
        const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
          method: "POST",
          body: formData
        });
        const data = await res.json();
        receiptURL = data.secure_url;
      } catch (err) {
        console.error("Receipt upload failed", err);
      }
    }

    // Create detailed items string with price and shipping
    const itemsList = cart.map(item => `${item.quantity}x ${item.name} (₦${item.price.toLocaleString()})`).join('\n');
    const itemsString = shippingCost > 0 ? `${itemsList}\nShipping (₦${shippingCost.toLocaleString()})` : itemsList;

    // Save Order to LocalStorage for Demo purposes
    const newOrder: Order = {
      id: generatedId,
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      total: cartTotal + shippingCost,
      status: paymentMethod === 'transfer' ? 'Pending Verification' : 'Processing',
      items: itemsString
    };

    const existingOrders = JSON.parse(localStorage.getItem('kano_fresh_orders') || '[]');
    localStorage.setItem('kano_fresh_orders', JSON.stringify([newOrder, ...existingOrders]));

    // Send to Backend (This triggers the email on the server)
    try {
      await createOrder({
        ...newOrder,
        email: details.email,
        shippingDetails: details,
        paymentRef,
        receiptURL // Pass the uploaded receipt URL to backend
      });
    } catch (error) {
      console.error("Failed to sync order with backend:", error);
    }

    if (paymentMethod === 'online') {
      paymentMsg = `Payment Verified via Paystack. Ref: ${paymentRef}`;
    } else if (paymentMethod === 'transfer') {
      paymentMsg = `Receipt Uploaded: ${receiptFile?.name || 'Yes'}. \nSTATUS: PENDING VERIFICATION. \nWe will review your receipt and confirm your order via email shortly.`;
    } else {
      paymentMsg = "Please have cash ready upon delivery.";
    }

    alert(`Order Placed Successfully!\n\nPayment Method: ${paymentMethod === 'pod' ? 'Pay on Delivery' : paymentMethod === 'online' ? 'Card Payment' : 'Bank Transfer'}\n${paymentMsg}\n\nYour Tracking ID is: ${generatedId}\n\nWe have sent a confirmation to your email: ${details.email}.\nPlease keep this ID safe to track your order.`);
    
    clearCart();
    
    if (user) {
      navigate('/userdashboard');
    } else {
      navigate('/');
    }
  };

  // Paystack Configuration
  const paystackConfig = {
    reference: (new Date()).getTime().toString(),
    email: details.email,
    amount: (cartTotal + shippingCost) * 100, // Amount in Kobo
    publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '',
    onSuccess: (reference: any) => finalizeOrder(reference.reference),
    onClose: () => alert('Payment cancelled.'),
  };

  const initializePayment = usePaystackPayment(paystackConfig);

  const handlePlaceOrder = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (paymentMethod === 'online') {
      // Trigger Paystack Modal
      initializePayment();
    } else if (paymentMethod === 'transfer') {
      // Handle Transfer with Receipt
      const file = receiptRef.current?.files?.[0];
      finalizeOrder(undefined, file);
    } else {
      // Handle Pay on Delivery
      finalizeOrder();
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-secondary">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-serif font-bold text-primary">Your cart is empty</h2>
          <button onClick={() => navigate('/')} className="mt-4 text-accent hover:underline">Go back to shop</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-serif font-bold text-primary mb-8">Checkout</h1>
        
        {/* Guest Checkout Notice */}
        {!user && (
          <div className="mb-8 bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
             <div>
               <p className="text-sm font-sans text-gray-600">Already have an account? <span className="hidden sm:inline">Log in for a faster checkout experience.</span></p>
             </div>
             <Link to="/login" className="text-sm font-medium text-primary border border-primary px-4 py-2 rounded hover:bg-gray-50 transition">
               Log In
             </Link>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Shipping Details Form */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h2 className="text-xl font-serif font-semibold mb-4">Shipping & Payment</h2>
            <form id="checkout-form" onSubmit={handlePlaceOrder} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input type="text" name="name" value={details.name} onChange={handleInputChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                <input type="email" name="email" value={details.email} onChange={handleInputChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Delivery Address</label>
                <input type="text" name="address" value={details.address} onChange={handleInputChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">State (Determines Shipping)</label>
                <select 
                  value={shippingState} 
                  onChange={(e) => setShippingState(e.target.value)}
                  required 
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                >
                  <option value="">Select State</option>
                  <option value="Kano">Kano</option>
                  <option value="Abuja">Abuja</option>
                  <option value="Lagos">Lagos</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input type="tel" name="phone" value={details.phone} onChange={handleInputChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
              </div>

              {/* Payment Method Section */}
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Method</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="pod"
                      name="paymentMethod"
                      type="radio"
                      checked={paymentMethod === 'pod'}
                      onChange={() => setPaymentMethod('pod')}
                      className="focus:ring-primary h-4 w-4 text-primary border-gray-300"
                    />
                    <label htmlFor="pod" className="ml-3 block text-sm font-medium text-gray-700">
                      Pay on Delivery (Cash/POS)
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="online"
                      name="paymentMethod"
                      type="radio"
                      checked={paymentMethod === 'online'}
                      onChange={() => setPaymentMethod('online')}
                      className="focus:ring-primary h-4 w-4 text-primary border-gray-300"
                      disabled={!paystackConfig.publicKey}
                    />
                    <label htmlFor="online" className={`ml-3 block text-sm font-medium ${!paystackConfig.publicKey ? 'text-gray-400' : 'text-gray-700'}`}>
                      Pay with Card {!paystackConfig.publicKey && '(Coming Soon)'}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="transfer"
                      name="paymentMethod"
                      type="radio"
                      checked={paymentMethod === 'transfer'}
                      onChange={() => setPaymentMethod('transfer')}
                      className="focus:ring-primary h-4 w-4 text-primary border-gray-300"
                    />
                    <label htmlFor="transfer" className="ml-3 block text-sm font-medium text-gray-700">
                      Bank Transfer
                    </label>
                  </div>
                  
                  {/* Bank Transfer Details & Verification */}
                  {paymentMethod === 'transfer' && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-md border border-gray-200 animate-fade-in">
                      <p className="text-sm text-gray-700 font-medium mb-2">Bank Details:</p>
                      <div className="text-sm text-gray-600 mb-4 space-y-1">
                        <p>Bank: <span className="font-bold text-gray-900">Zenith Bank</span></p>
                        <p>Account Number: <span className="font-bold text-gray-900">1234567890</span></p>
                        <p>Account Name: <span className="font-bold text-gray-900">Mokagz Fresh Produce</span></p>
                      </div>
                      <label htmlFor="transferReceipt" className="block text-sm font-medium text-gray-700 mb-1">
                        Upload Payment Receipt (Screenshot/PDF)
                      </label>
                      <input 
                        type="file" 
                        ref={receiptRef}
                        accept="image/*,.pdf"
                        required 
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-green-800" 
                      />
                      <p className="text-xs text-gray-500 mt-2">Please upload a screenshot of your transfer to confirm your order.</p>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-fit">
            <h2 className="text-xl font-serif font-semibold mb-4">Order Summary</h2>
            <ul className="divide-y divide-gray-200 mb-4">
              {cart.map((item) => (
                <li key={item.id} className="py-3 flex justify-between items-center">
                  <div className="flex-1">
                    <span className="text-gray-800 text-sm font-medium block">{item.name}</span>
                    <span className="text-gray-500 text-xs">₦{item.price.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex items-center border border-gray-300 rounded mx-2">
                    <button 
                      type="button"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="px-2 py-1 hover:bg-gray-100 text-gray-600 text-xs disabled:opacity-50"
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span className="px-2 text-sm font-medium min-w-[1.5rem] text-center">{item.quantity}</span>
                    <button 
                      type="button"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="px-2 py-1 hover:bg-gray-100 text-gray-600 text-xs"
                    >
                      +
                    </button>
                  </div>

                  <div className="text-right">
                    <span className="text-gray-900 font-medium block">₦{(item.price * item.quantity).toLocaleString()}</span>
                    <button 
                      type="button"
                      onClick={() => removeFromCart(item.id)}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span>₦{cartTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Shipping ({shippingState || 'Pending'})</span>
                <span>₦{shippingCost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-primary pt-2 border-t border-gray-100 mt-2">
                <span>Total</span>
                <span>₦{(cartTotal + shippingCost).toLocaleString()}</span>
              </div>
            </div>

            <button form="checkout-form" type="submit" className="w-full mt-6 bg-accent text-white py-3 rounded-md font-medium hover:bg-yellow-600 transition shadow-sm">
              Place Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;